import { useEffect, useState } from "react";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCheckBox from "views/system-design/DSCheckBox";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSErrorButton from "views/system-design/DSErrorButton";
import { Author } from "../../../../api/author";
import { DeleteUserResult } from "../../../../api/admin";
import { useUserContent } from "../hooks/useUserContent";
import useDeleteUserMutation from "../hooks/useDeleteUserMutation";

type DeleteUserModalProps = {
  user: Author | null;
  onClose: () => void;
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
    <DSTypography variant="subtitle2" sx={{ fontWeight: "bold" }}>
      {title}
    </DSTypography>
    {children}
  </DSBox>
);

const DeleteUserModal = ({ user, onClose }: DeleteUserModalProps) => {
  const open = !!user;
  const userId = user?.id ?? "";

  const contentQuery = useUserContent(userId, open);

  // A user can never be deleted while leaving content behind, so there is no
  // per-item selection: all of their content is always deleted, gated behind a
  // single confirmation checkbox.
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [result, setResult] = useState<DeleteUserResult | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutateAsync: deleteUser, isPending } = useDeleteUserMutation();

  const collections = contentQuery.data?.collections ?? [];
  const klydos = contentQuery.data?.klydos ?? [];
  const metaobject = contentQuery.data?.artistMetaobject ?? null;
  const collaboratorId = contentQuery.data?.collaboratorId ?? null;

  // Reset confirmation whenever content is (re)fetched so a stale check can't
  // silently gate a delete over a content list the admin hasn't reviewed.
  useEffect(() => {
    setConfirmDeleteAll(false);
  }, [contentQuery.data]);

  const ownedCollectionIds = new Set(collections.map((c) => c.id));

  const isLoading = contentQuery.isLoading;
  const hasLoadError = contentQuery.isError;
  const hasResources =
    collections.length > 0 || klydos.length > 0 || !!metaobject;

  const reset = () => {
    setConfirmDeleteAll(false);
    setResult(null);
    setDeleteError(null);
  };

  const handleClose = () => {
    if (isPending) return;
    reset();
    onClose();
  };

  const handleDelete = async () => {
    setDeleteError(null);
    try {
      const res = await deleteUser({
        userId,
        options: {
          klydoIds: klydos.map((k) => k.id),
          collectionIds: collections.map((c) => c.id),
          deleteArtistMetaobject: !!metaobject,
        },
      });
      if (res.failed.length === 0) {
        handleClose();
      } else {
        // Keep the dialog open and surface what could not be deleted.
        setResult(res);
      }
    } catch (e) {
      // The request itself failed (network / non-2xx) — keep the dialog open
      // and show the error instead of silently sitting on the form.
      setDeleteError(
        e instanceof Error ? e.message : "Failed to delete the user.",
      );
    }
  };

  const userLabel = user ? user.name || user.email || user.id : "";

  // Spell out what the confirmation covers, so the admin sees exactly what the
  // single checkbox will permanently delete.
  const confirmParts: string[] = [];
  if (collections.length)
    confirmParts.push(
      `${collections.length} collection${collections.length === 1 ? "" : "s"}`,
    );
  if (klydos.length)
    confirmParts.push(
      `${klydos.length} klydo${klydos.length === 1 ? "" : "s"}`,
    );
  if (metaobject) confirmParts.push("the Shopify artist meta-object");
  const confirmLabel = `I understand the user's ${confirmParts.join(
    ", ",
  )} will be permanently deleted.`;

  const deleteDisabled =
    isPending ||
    isLoading ||
    hasLoadError ||
    (hasResources && !confirmDeleteAll);

  return (
    <DSDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DSDialogTitle>
        Delete user{userLabel ? `: ${userLabel}` : ""}
      </DSDialogTitle>
      <DSDialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: "12px !important",
        }}
      >
        {result ? (
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <DSTypography>
              The user was processed, but some items could not be deleted:
            </DSTypography>
            {result.failed.map((item) => (
              <DSTypography
                key={`${item.type}-${item.id}`}
                variant="body2"
                sx={{ color: "error.main" }}
              >
                • {item.type} {item.id}: {item.error}
              </DSTypography>
            ))}
            {result.collaboratorId && (
              <DSTypography variant="body2">
                CollabPay collaborator {result.collaboratorId} was not deleted.
              </DSTypography>
            )}
            {result.deleted.length > 0 && (
              <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
                Successfully deleted {result.deleted.length} item
                {result.deleted.length === 1 ? "" : "s"}.
              </DSTypography>
            )}
          </DSBox>
        ) : isLoading ? (
          <DSBox sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <DSCircularProgress />
          </DSBox>
        ) : hasLoadError ? (
          <DSTypography sx={{ color: "error.main" }}>
            ⚠ Failed to load this user&apos;s content. Please try again before
            deleting so you can review what they own.
          </DSTypography>
        ) : (
          <>
            <DSTypography>
              This will permanently delete the user&apos;s account (auth,
              database and Firestore)
              {hasResources
                ? " together with all of the content listed below."
                : "."}
            </DSTypography>

            <Section title="Collections">
              {collections.length === 0 ? (
                <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
                  No collections.
                </DSTypography>
              ) : (
                collections.map((c) => (
                  <DSTypography key={c.id} variant="body2">
                    • {c.name} — {c.klydoCount} klydo
                    {c.klydoCount === 1 ? "" : "s"}
                    {c.isPublishedToShopify ? " · published to Shopify" : ""}
                  </DSTypography>
                ))
              )}
            </Section>

            <Section title="Klydos">
              {klydos.length === 0 ? (
                <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
                  No klydos.
                </DSTypography>
              ) : (
                klydos.map((k) => (
                  <DSBox key={k.id}>
                    <DSTypography variant="body2">
                      • {k.name || k.id}
                    </DSTypography>
                    {k.collectionId && (
                      <DSTypography
                        variant="caption"
                        sx={{ display: "block", ml: 2, color: "warning.main" }}
                      >
                        ⚠ Belongs to collection &quot;
                        {k.collectionName ?? k.collectionId}&quot;
                        {ownedCollectionIds.has(k.collectionId)
                          ? " — deleting this klydo will also delete that collection."
                          : " — owned by another artist; deleting this klydo just removes it from that collection."}
                      </DSTypography>
                    )}
                  </DSBox>
                ))
              )}
            </Section>

            <Section title="Shopify artist meta-object">
              {metaobject ? (
                <DSTypography variant="body2">
                  • {metaobject.name ?? metaobject.id}
                </DSTypography>
              ) : (
                <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
                  No Shopify artist meta-object.
                </DSTypography>
              )}
            </Section>

            {collaboratorId && (
              <Section title="CollabPay collaborator">
                <DSTypography variant="body2">
                  • {collaboratorId} — not deleted. It stays in CollabPay with
                  its payouts; note this id if it needs to be handled later.
                </DSTypography>
              </Section>
            )}

            {hasResources && (
              <DSFormControlLabel
                control={
                  <DSCheckBox
                    checked={confirmDeleteAll}
                    onChange={(e) => setConfirmDeleteAll(e.target.checked)}
                  />
                }
                label={confirmLabel}
              />
            )}

            {deleteError && (
              <DSTypography sx={{ color: "error.main" }}>
                ⚠ {deleteError}
              </DSTypography>
            )}
          </>
        )}
      </DSDialogContent>
      <DSDialogActions>
        {result ? (
          <DSPrimaryButton onClick={handleClose}>Close</DSPrimaryButton>
        ) : (
          <>
            <DSSecondaryButton onClick={handleClose} disabled={isPending}>
              Cancel
            </DSSecondaryButton>
            <DSErrorButton onClick={handleDelete} disabled={deleteDisabled}>
              {isPending ? "Deleting..." : "Delete user"}
            </DSErrorButton>
          </>
        )}
      </DSDialogActions>
    </DSDialog>
  );
};

export default DeleteUserModal;
